import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionHead } from "./Reveal";

export default function YoutubeSlider({ videos = [] }) {
  const rowRef = useRef(null);
  const [playing, setPlaying] = useState(null);
  const [broken, setBroken] = useState({});

  const scroll = (dir) => {
    const el = rowRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <section id="youtube" className="py-24 bg-ink text-white grain relative overflow-hidden">
      <div className="absolute -top-32 left-1/3 h-96 w-96 rounded-full bg-red-500/10 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHead dark index="04" label="YouTube Hub"
            title="Free video lessons & course previews"
            sub="Watch sample lessons from the channel — full courses unlock in the store." />
          <div className="flex gap-3">
            <button onClick={() => scroll(-1)} data-testid="youtube-scroll-prev" aria-label="Scroll left"
              className="rounded-full border border-white/15 bg-white/5 p-3.5 text-white backdrop-blur hover:border-amber-400/60 hover:text-amber-400 transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => scroll(1)} data-testid="youtube-scroll-next" aria-label="Scroll right"
              className="rounded-full border border-white/15 bg-white/5 p-3.5 text-white backdrop-blur hover:border-amber-400/60 hover:text-amber-400 transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div ref={rowRef} className="no-scrollbar mt-12 flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2" data-testid="youtube-slider-container">
          {videos.length === 0 && (
            <p className="text-slate-500 text-sm py-10">No videos added yet — admin panel se apne YouTube links jodein.</p>
          )}
          {videos.map((v) => v.kind === "link" ? (
            <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" data-testid={`link-card-${v.id}`}
              className="group relative flex w-[280px] sm:w-[360px] shrink-0 snap-start flex-col overflow-hidden rounded-3xl border border-amber-500/25 bg-ink2 text-left transition-colors hover:border-amber-500/60">
              <div className="relative flex h-28 items-center justify-center bg-gradient-to-br from-amber-500/15 via-ink2 to-ink overflow-hidden">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-ink shadow-xl shadow-amber-500/30 transition-transform duration-300 group-hover:scale-110"><ExternalLink size={22} /></span>
                <span className="absolute bottom-3 right-3 rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink" data-testid={`link-card-domain-${v.id}`}>{(() => { try { return new URL(v.url).hostname.replace("www.", ""); } catch { return "link"; } })()}</span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <p className="font-display text-sm font-bold leading-snug line-clamp-2">{v.title}</p>
                {v.description && <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-400">{v.description}</p>}
                <p className="mt-auto pt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-400">Click karo — {(() => { try { return new URL(v.url).hostname.replace("www.", ""); } catch { return "site"; } })()} par jayega →</p>
              </div>
            </a>
          ) : (
            <button
              key={v.id}
              onClick={() => setPlaying(v)}
              data-testid={`youtube-card-${v.id}`}
              className="group relative w-[280px] sm:w-[360px] shrink-0 snap-start overflow-hidden rounded-3xl border border-white/10 bg-ink2 text-left"
            >
              <div className="relative aspect-video overflow-hidden">
                {broken[v.id] ? (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-ink2 via-ink to-ink2">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20 text-amber-400"><Play size={22} fill="currentColor" className="ml-0.5" /></span>
                  </div>
                ) : (
                  <img
                    src={`https://i.ytimg.com/vi/${v.video_id}/hqdefault.jpg`}
                    alt={v.title}
                    loading="lazy"
                    onError={() => setBroken((b) => ({ ...b, [v.id]: true }))}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-ink shadow-xl shadow-amber-500/30 transition-transform duration-300 group-hover:scale-110">
                    <Play size={22} fill="currentColor" className="ml-0.5" />
                  </span>
                </span>
                <span className="absolute bottom-3 right-3 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">YouTube</span>
              </div>
              <div className="p-4">
                <p className="font-display text-sm font-bold leading-snug line-clamp-2">{v.title}</p>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Tap to play</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-4xl border-white/10 bg-ink p-2" data-testid="youtube-modal">
          {playing && (
            <div>
              <DialogHeader className="flex flex-row items-center justify-between px-4 pt-2">
                <DialogTitle className="font-display text-base font-bold text-white text-left max-w-md">{playing.title}</DialogTitle>
                <a href={`https://www.youtube.com/watch?v=${playing.video_id}`} target="_blank" rel="noopener noreferrer"
                  data-testid="youtube-external-link"
                  className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-red-500">
                  <ExternalLink size={13} /> Watch on YouTube
                </a>
              </DialogHeader>
              <div className="mt-2 aspect-video overflow-hidden rounded-xl">
                <iframe
                  src={`https://www.youtube.com/embed/${playing.video_id}?autoplay=1&rel=0`}
                  title={playing.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
              <p className="px-4 py-2 text-[11px] text-slate-500">Video chal nahi raha? Upar "Watch on YouTube" daba kar seedha YouTube par dekhein.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
